// frontend/src/components/Individual/Dashboard.jsx
import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '../../services/api';
import VoiceEnrollment from './VoiceEnrollment';
import { hasFingerprint } from '../../utils/speakerDetector';

const IndividualDashboard = ({ user, onLogout }) => {
  const navigate = useNavigate();
  const [stats, setStats] = useState({ sessions: 0, transcripts: 0, activeRooms: 0 });
  const [showEnrollment, setShowEnrollment] = useState(false);
  const [voiceTrained, setVoiceTrained] = useState(hasFingerprint());
  const [cloudBackup, setCloudBackup] = useState(false);
  const [cloudBackupLoading, setCloudBackupLoading] = useState(false);
  const [cloudConfigured, setCloudConfigured] = useState(false);

  useEffect(() => {
    const fetchStats = async () => {
      try {
        const [sessionsRes, transcriptsRes, roomsRes, userSettingsRes] = await Promise.all([
          api.get('/sessions/user'),
          api.get('/transcripts'),
          api.get('/rooms/user/my-rooms'),
          api.get('/users/me/settings'),
          api.get('/settings/session').catch(() => ({ data: {} })) // non-critical
        ]);
        setStats({
          sessions: sessionsRes.data.sessions?.length ?? 0,
          transcripts: transcriptsRes.data.total ?? 0,
          activeRooms: roomsRes.data.rooms?.filter(r => r.isActive).length ?? 0
        });
        setCloudBackup(userSettingsRes.data.cloudBackupEnabled ?? false);
        // If cloud is configured (provider set), show the toggle
        setCloudConfigured(true);
      } catch (error) {
        console.error('Error fetching stats:', error);
      }
    };

    fetchStats();
  }, []);

  const handleCloudBackupToggle = async () => {
    setCloudBackupLoading(true);
    try {
      const res = await api.put('/users/me/cloud-backup', { enabled: !cloudBackup });
      setCloudBackup(res.data.cloudBackupEnabled);
    } catch (e) {
      console.error('Failed to toggle cloud backup:', e);
    } finally {
      setCloudBackupLoading(false);
    }
  };

  const menuItems = [
    {
      title: 'History',
      description: 'View your past transcripts and recordings',
      icon: (
        <svg className="h-12 w-12" fill="none" viewBox="0 0 24 24" stroke="currentColor">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
        </svg>
      ),
      color: 'from-blue-500 to-blue-600',
      route: '/history'
    },
    {
      title: 'Join Room',
      description: 'Enter a session key to join an existing room',
      icon: (
        <svg className="h-12 w-12" fill="none" viewBox="0 0 24 24" stroke="currentColor">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 16l-4-4m0 0l4-4m-4 4h14m-5 4v1a3 3 0 01-3 3H6a3 3 0 01-3-3V7a3 3 0 013-3h7a3 3 0 013 3v1" />
        </svg>
      ),
      color: 'from-purple-500 to-purple-600',
      route: '/join-room'
    },
    {
      title: 'Create Room',
      description: 'Start a new transcription session and share with others',
      icon: (
        <svg className="h-12 w-12" fill="none" viewBox="0 0 24 24" stroke="currentColor">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6v6m0 0v6m0-6h6m-6 0H6" />
        </svg>
      ),
      color: 'from-pink-500 to-pink-600',
      route: '/create-room'
    }
  ];

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 via-purple-50 to-pink-50">
      {showEnrollment && (
        <VoiceEnrollment
          onClose={() => setShowEnrollment(false)}
          onEnrolled={() => { setVoiceTrained(true); setShowEnrollment(false); }}
        />
      )}
      {/* Header */}
      <nav className="bg-white shadow-sm">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between h-16">
            <div className="flex items-center">
              <div className="flex-shrink-0 flex items-center">
                <div className="bg-gradient-to-r from-blue-500 to-purple-600 rounded-full p-2">
                  <svg className="h-8 w-8 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 11a7 7 0 01-7 7m0 0a7 7 0 01-7-7m7 7v4m0 0H8m4 0h4m-4-8a3 3 0 01-3-3V5a3 3 0 116 0v6a3 3 0 01-3 3z" />
                  </svg>
                </div>
                <span className="ml-3 text-xl font-bold text-gray-800">Voice to Text</span>
              </div>
            </div>
            <div className="flex items-center space-x-4">
              <div className="text-right">
                <p className="text-sm font-medium text-gray-900">
                  {user.firstName} {user.lastName}
                </p>
                <p className="text-xs text-gray-500">{user.email}</p>
              </div>
              <button
                onClick={onLogout}
                className="px-4 py-2 bg-red-500 text-white rounded-lg hover:bg-red-600 transition-colors"
              >
                Logout
              </button>
            </div>
          </div>
        </div>
      </nav>

      {/* Main Content */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        {/* Welcome Section */}
        <div className="text-center mb-12">
          <h1 className="text-4xl font-bold text-gray-900 mb-4">
            Welcome back, {user.firstName}!
          </h1>
          <p className="text-xl text-gray-600">
            What would you like to do today?
          </p>
        </div>

        {/* Voice Training Banner */}
        <div className={`mb-8 rounded-2xl p-5 flex items-center justify-between shadow-lg ${
          voiceTrained
            ? 'bg-gradient-to-r from-green-500 to-emerald-600 text-white'
            : 'bg-gradient-to-r from-indigo-500 to-purple-600 text-white'
        }`}>
          <div className="flex items-center space-x-4">
            <div className="w-12 h-12 rounded-full bg-white bg-opacity-20 flex items-center justify-center flex-shrink-0">
              <svg className="h-6 w-6 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z" />
              </svg>
            </div>
            <div>
              <p className="font-bold text-lg">
                {voiceTrained ? 'Voice Trained' : 'Train Your Voice'}
              </p>
              <p className="text-sm text-white text-opacity-80">
                {voiceTrained
                  ? 'AI ignores your voice and only answers the interviewer'
                  : 'Record 8s so AI recognises your voice and only answers the interviewer'}
              </p>
            </div>
          </div>
          <button
            onClick={() => setShowEnrollment(true)}
            className="ml-4 px-5 py-2.5 bg-white text-indigo-700 font-semibold rounded-xl hover:bg-opacity-90 transition-all shadow text-sm flex-shrink-0"
          >
            {voiceTrained ? 'Re-train' : 'Train Now'}
          </button>
        </div>

        {/* Cloud Backup Banner */}
        {cloudConfigured && (
          <div className={`mb-8 rounded-2xl p-5 flex items-center justify-between shadow-lg ${
            cloudBackup
              ? 'bg-gradient-to-r from-blue-500 to-indigo-600 text-white'
              : 'bg-gradient-to-r from-gray-400 to-gray-500 text-white'
          }`}>
            <div className="flex items-center space-x-4">
              <div className="w-12 h-12 rounded-full bg-white bg-opacity-20 flex items-center justify-center flex-shrink-0">
                <svg className="h-6 w-6 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M15 13l-3-3m0 0l-3 3m3-3v12" />
                </svg>
              </div>
              <div>
                <p className="font-bold text-lg">
                  {cloudBackup ? 'Cloud Backup ON' : 'Cloud Backup OFF'}
                </p>
                <p className="text-sm text-white text-opacity-80">
                  {cloudBackup
                    ? 'Your transcripts are being saved to the cloud (daily file per session)'
                    : 'Enable to automatically save your transcripts to cloud storage'}
                </p>
              </div>
            </div>
            <button
              onClick={handleCloudBackupToggle}
              disabled={cloudBackupLoading}
              className={`ml-4 px-5 py-2.5 font-semibold rounded-xl transition-all shadow text-sm flex-shrink-0 disabled:opacity-60 ${
                cloudBackup
                  ? 'bg-white text-indigo-700 hover:bg-opacity-90'
                  : 'bg-white text-gray-700 hover:bg-opacity-90'
              }`}
            >
              {cloudBackupLoading ? '...' : cloudBackup ? 'Disable' : 'Enable'}
            </button>
          </div>
        )}

        {/* Menu Cards */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-8 mb-12">
          {menuItems.map((item, index) => (
            <div
              key={index}
              onClick={() => navigate(item.route)}
              className="bg-white rounded-2xl shadow-xl overflow-hidden cursor-pointer transform hover:scale-105 transition-all duration-300 hover:shadow-2xl"
            >
              <div className={`bg-gradient-to-r ${item.color} p-8 text-white`}>
                <div className="flex justify-center mb-4">
                  {item.icon}
                </div>
                <h3 className="text-2xl font-bold text-center">{item.title}</h3>
              </div>
              <div className="p-6">
                <p className="text-gray-600 text-center">{item.description}</p>
                <div className="mt-6 flex justify-center">
                  <button className="px-6 py-2 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 transition-colors font-medium">
                    Get Started →
                  </button>
                </div>
              </div>
            </div>
          ))}
        </div>

        {/* Quick Stats */}
        <div className="bg-white rounded-2xl shadow-xl p-8">
          <h2 className="text-2xl font-bold text-gray-800 mb-6">Your Activity</h2>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            <div className="bg-gradient-to-r from-blue-50 to-blue-100 rounded-xl p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-blue-600 font-medium">Active Sessions</p>
                  <p className="text-3xl font-bold text-blue-900 mt-2">{stats.sessions}</p>
                </div>
                <div className="bg-blue-500 rounded-full p-3">
                  <svg className="h-8 w-8 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
                  </svg>
                </div>
              </div>
            </div>

            <div className="bg-gradient-to-r from-purple-50 to-purple-100 rounded-xl p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-purple-600 font-medium">Transcripts</p>
                  <p className="text-3xl font-bold text-purple-900 mt-2">{stats.transcripts}</p>
                </div>
                <div className="bg-purple-500 rounded-full p-3">
                  <svg className="h-8 w-8 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                  </svg>
                </div>
              </div>
            </div>

            <div className="bg-gradient-to-r from-pink-50 to-pink-100 rounded-xl p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-pink-600 font-medium">Active Rooms</p>
                  <p className="text-3xl font-bold text-pink-900 mt-2">{stats.activeRooms}</p>
                </div>
                <div className="bg-pink-500 rounded-full p-3">
                  <svg className="h-8 w-8 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z" />
                  </svg>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Company Info */}
        <div className="mt-8 bg-gradient-to-r from-gray-100 to-gray-200 rounded-xl p-6 text-center">
          <p className="text-gray-600">
            Company: <span className="font-semibold text-gray-800">{user.companyName}</span>
          </p>
        </div>
      </div>
    </div>
  );
};

export default IndividualDashboard;
